import React, { MutableRefObject, PropsWithChildren, ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import { createPortal } from 'react-dom';

import s from './index.module.css';

interface ILayoutWithFooterProps {
  // you can just specify footer prop if you don't need in Context
  footer?: ReactNode;
  children:
    | ReactNode
    | (({ footerComponent }: { footerComponent: ({ children }: PropsWithChildren) => JSX.Element }) => ReactNode);
}

function LayoutWithFooter({ children, footer }: ILayoutWithFooterProps) {
  const isRenderPropsMode = typeof children === 'function';

  if (isRenderPropsMode && footer !== undefined) {
    throw new Error('Please specify only footer props OR children as a arrow function');
  }

  const footerRef = useRef(null);
  const FooterComponent = useCallback(
    ({ children }: PropsWithChildren) => <FooterWrapper mountPoint={footerRef}>{children}</FooterWrapper>,
    [],
  );

  const body = isRenderPropsMode ? children({ footerComponent: FooterComponent }) : children;

  return (
    <div className={s.container}>
      <div className={s.body}>{body}</div>
      <div className={s.footer} ref={footerRef}>
        {isRenderPropsMode ? null : footer}
      </div>
    </div>
  );
}

export default LayoutWithFooter;

interface IFooterWrapperProps extends PropsWithChildren {
  mountPoint: MutableRefObject<HTMLDivElement | null>;
}

function FooterWrapper({ children, mountPoint }: IFooterWrapperProps) {
  const [elem, setElem] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    setElem(mountPoint.current);
  }, [mountPoint]);

  if (!elem) return null;

  return createPortal(children, elem);
}
